import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenant from '../../config/tenant';
import { getAuthKeyFromLocalStorage } from './employeeSlice';


async function attendanceSummaryFetch(url, token) {
  console.debug(`[attendanceSummaryFetch] GET ${url}`);
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['auth-key'] = token;
  }

  const res = await fetch(url, {
    method: 'GET',
    headers,
  });

  console.debug(`[attendanceSummaryFetch] Response status: ${res.status}`);

  if (res.status === 404) {
    return { __noData: true };
  }

  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text || `HTTP error ${res.status}`);
  }

  const json = await res.json();
  console.debug('[attendanceSummaryFetch] Response JSON:', json);
  return json;
}


export async function getEmployeesAttendanceRows({ fromDate, toDate }) {
  const token = getAuthKeyFromLocalStorage();

  const url = `${tenant.api.baseUrl}/Attendance/GetEmployeesAttendance?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
  const data = await attendanceSummaryFetch(url, token);

  if (data && data.__noData) return [];

  if (data && data.StatusCode !== undefined && data.StatusCode !== 200) {
    if (data.StatusCode === 404 || /no\s*(data|record)/i.test(data.Message || '')) return [];
    throw new Error(data.Message || `Server error: status ${data.StatusCode}`);
  }

  if (Array.isArray(data)) return data;

  const resultSet = data ? (data.ResultSet ?? data.Result ?? data.Data) : null;
  return Array.isArray(resultSet) ? resultSet : (resultSet ? [resultSet] : []);
}

export const fetchEmployeesAttendance = createAsyncThunk(
  'attendanceSummary/fetchEmployeesAttendance',
  async ({ fromDate, toDate }, { rejectWithValue }) => {
    if (!fromDate) return rejectWithValue('From date is required.');
    if (!toDate)   return rejectWithValue('To date is required.');

    try {
      const rows = await getEmployeesAttendanceRows({ fromDate, toDate });
      console.info(`[attendanceSummary] Loaded ${rows.length} attendance rows (${fromDate} → ${toDate})`);
      return { fromDate, toDate, rows };
    } catch (err) {
      console.error('[attendanceSummary]  fetchEmployeesAttendance error:', err);
      return rejectWithValue(err.message || 'Network error. Check your connection.');
    }
  }
);

const initialState = {
  fromDate: null,
  toDate:   null,
  rows:     [],
  loading:  false,
  error:    null,
};

const attendanceSummarySlice = createSlice({
  name: 'attendanceSummary',
  initialState,
  reducers: {
    clearAttendanceSummaryError(state) {
      state.error = null;
    },
    resetAttendanceSummary(state) {
      state.fromDate = null;
      state.toDate   = null;
      state.rows     = [];
      state.error    = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeesAttendance.pending, (state, action) => {
        state.loading  = true;
        state.error    = null;
        state.fromDate = action.meta.arg.fromDate;
        state.toDate   = action.meta.arg.toDate;
      })
      .addCase(fetchEmployeesAttendance.fulfilled, (state, action) => {
        const { fromDate, toDate, rows } = action.payload;
        state.loading  = false;
        state.fromDate = fromDate;
        state.toDate   = toDate;
        state.rows     = rows;
      })
      .addCase(fetchEmployeesAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload ?? 'Failed to load attendance summary';
        state.rows    = [];
      });
  },
});

export const { clearAttendanceSummaryError, resetAttendanceSummary } = attendanceSummarySlice.actions;
export default attendanceSummarySlice.reducer;
